// components/legal/RefundPolicy.js
import React, { useEffect } from 'react';
import styled from 'styled-components';
import Container from '../common/Container';
import { SectionTitle } from '../common/SharedStyles';
import Button from '../common/Button';
import { Link } from 'react-router-dom';

const LegalSection = styled.section`
  padding: 6rem 0;
  background-color: var(--background-primary);
`;

const LegalContainer = styled(Container)`
  max-width: 800px;
`;

const EffectiveDate = styled.p`
  font-style: italic;
  color: var(--text-secondary);
  margin-bottom: 2rem;
  text-align: center;
`;

const SectionHeading = styled.h2`
  color: var(--accent-secondary);
  margin-top: 2.5rem;
  margin-bottom: 1rem;
  font-size: 1.5rem;
`;

const Paragraph = styled.p`
  margin-bottom: 1rem;
  line-height: 1.7;
`;

const BulletList = styled.ul`
  margin: 1rem 0;
  padding-left: 1.5rem;

  li {
    margin-bottom: 0.75rem;
    line-height: 1.7;
  }
`;

const BackButton = styled(Button)`
  margin-top: 3rem;
`;

const RefundPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <LegalSection id="refund-policy">
      <LegalContainer>
        <SectionTitle>Refund Policy</SectionTitle>
        <EffectiveDate>Effective Date: March 26th, 2026</EffectiveDate>

        <Paragraph>
          This Refund Policy outlines the billing practices, refund guidelines, and subscription
          management terms for the software and services provided by Signage LLC ("Signage," "we,"
          "our," or "us"). By purchasing, subscribing to, or using our services, you acknowledge and agree
          to the policies described below regarding refunds, billing cycles, subscription cancellations, and
          dispute resolution.
        </Paragraph>
        <Paragraph>
          For clarity and transparency, all charges will appear on your billing statement as "Signage LLC."
          If you have any questions regarding billing, subscriptions, or charges associated with your
          account, please review the information below or contact our support team for assistance.
        </Paragraph>

        <SectionHeading>Refund Policy</SectionHeading>
        <BulletList>
          <li>
            Refunds may be issued in rare circumstances, such as billing errors, mistaken card charges, or
            verified technical issues that prevent proper use of the service.
          </li>
          <li>
            Each refund request is reviewed individually, and refunds may be granted when deemed
            reasonable and appropriate based on the circumstances.
          </li>
          <li>
            While refunds are uncommon due to the digital nature of our software, we maintain high
            standards of quality and reliability to minimize the likelihood of such issues.
          </li>
        </BulletList>

        <SectionHeading>Subscription Billing</SectionHeading>
        <Paragraph>
          Our services operate on a recurring subscription basis. By subscribing, you authorize Signage
          LLC to charge your payment method on a recurring basis according to the selected
          subscription plan until the subscription is canceled.
        </Paragraph>

        <SectionHeading>How to Cancel Your Subscription</SectionHeading>
        <BulletList>
          <li>
            Users may cancel their subscription at any time through their account dashboard on the
            website.
          </li>
          <li>
            Subscriptions may also be canceled through the billing portal provided during checkout, which is
            managed securely through our payment processor.
          </li>
          <li>
            To avoid being charged for the next billing cycle, cancellations must be completed before the
            next billing date. Once canceled, the subscription will remain active until the end of the current
            billing period, after which no further charges will occur.
          </li>
        </BulletList>

        <SectionHeading>Chargeback Prevention &amp; Dispute Resolution</SectionHeading>
        <Paragraph>
          We are committed to resolving any billing concerns quickly and fairly. If you have questions
          about a charge or believe you were billed incorrectly, please contact our support team before
          initiating a chargeback with your bank or credit card provider.
        </Paragraph>
        <Paragraph>
          Filing a chargeback without first contacting us may delay resolution and may result in the
          suspension or termination of your account while the dispute is under review.
        </Paragraph>
        <Paragraph>
          Our team will work with you directly to review the situation and determine the appropriate
          resolution in accordance with this Refund Policy. Most billing concerns can be resolved quickly
          through our support channels without the need for a formal dispute.
        </Paragraph>
        <Paragraph>
          By purchasing or subscribing to our services, you agree to contact us first to attempt to resolve
          any billing issue prior to initiating a chargeback.
        </Paragraph>
        <Paragraph>
          All charges will appear on your billing statement as "Signage LLC."
        </Paragraph>

        <SectionHeading>Fraud Prevention &amp; Transaction Review</SectionHeading>
        <Paragraph>
          To protect our customers and maintain the integrity of our platform, all transactions are subject
          to automated and manual fraud prevention screening.
        </Paragraph>
        <Paragraph>
          We reserve the right to review any order, subscription, or account activity that appears unusual,
          high-risk, or inconsistent with normal purchasing behavior. This may include verifying billing
          details, requesting additional information, or temporarily delaying service activation while the
          review is completed.
        </Paragraph>
        <Paragraph>
          If a transaction is flagged by our fraud prevention systems, we may:
        </Paragraph>
        <BulletList>
          <li>Temporarily suspend or delay access to the service</li>
          <li>Request identity or billing verification</li>
          <li>Cancel or refund the transaction if it cannot be verified</li>
        </BulletList>
        <Paragraph>
          Accounts found to be associated with fraudulent activity, unauthorized payment methods, or
          abuse of our platform may be permanently suspended or terminated.
        </Paragraph>
        <Paragraph>
          Our goal is to protect both our customers and our business from unauthorized transactions
          while ensuring legitimate users can continue using the service without interruption.
        </Paragraph>

        <SectionHeading>Risk Compliance</SectionHeading>
        <Paragraph>
          We are committed to maintaining a secure, compliant, and trustworthy platform for all users. Our
          services and payment systems operate in accordance with applicable financial regulations,
          payment network requirements, and industry best practices.
        </Paragraph>
        <Paragraph>
          To protect our platform and customers, we may monitor account activity, transactions, and
          usage patterns to identify potential violations of our policies, fraud, or other prohibited activities.
        </Paragraph>
        <Paragraph>
          We reserve the right to restrict, suspend, or terminate accounts that are found to be in violation
          of our Terms of Service, engaging in fraudulent behavior, abusing the platform, or attempting to
          circumvent billing systems or security safeguards.
        </Paragraph>
        <Paragraph>
          In certain circumstances, we may cooperate with payment processors, financial institutions, or
          regulatory authorities to investigate suspicious activity or ensure compliance with applicable
          laws and payment network rules.
        </Paragraph>
        <Paragraph>
          Our goal is to maintain a safe environment for legitimate users while protecting the integrity of
          our services and payment infrastructure.
        </Paragraph>

        <SectionHeading>Customer Clarity</SectionHeading>
        <Paragraph>
          We strive to provide clear and transparent information about our services so customers fully
          understand what they are purchasing. Our platform provides digital tools designed to assist sign
          manufacturers with estimating, sourcing materials, and calculating project costs.
        </Paragraph>
        <Paragraph>
          Before subscribing, customers are encouraged to review all available product information,
          pricing details, and policy pages to ensure the service meets their needs.
        </Paragraph>
        <Paragraph>
          By purchasing or subscribing to our services, you acknowledge that you understand the nature
          of the service being provided, including the features available within the platform, the
          subscription billing structure, and the applicable policies outlined on this website.
        </Paragraph>
        <Paragraph>
          If you have any questions regarding our services, billing, or policies, we encourage you to
          contact our support team prior to making a purchase. We are always happy to clarify any details
          and ensure you have the information needed to make an informed decision.
        </Paragraph>
        <Paragraph>
          For any questions regarding billing, subscriptions, or platform functionality, please contact our
          support team at support@signage-llc.com.
        </Paragraph>

        <Link to="/">
          <BackButton variant="primary">Back to Website</BackButton>
        </Link>
      </LegalContainer>
    </LegalSection>
  );
};

export default RefundPolicy;
